/**
 * Piece Set Management System
 * 
 * Provides different visual styles for chess pieces using Unicode symbols
 * with font variations and styling
 */

import { PieceType, Color } from '../../engine/types';

// Piece set types
export type PieceSet = 'classic' | 'modern' | 'alpha' | 'cases';

// Piece set configuration interface
export interface PieceSetConfig {
  name: string;
  description: string;
  whitePieces: Record<PieceType, string>;
  blackPieces: Record<PieceType, string>;
  fontFamily?: string;
  fontSize?: string;
  fontWeight?: string;
}

/**
 * Available piece sets with their Unicode symbols and styling
 */
export const PIECE_SETS: Record<PieceSet, PieceSetConfig> = {
  // Classic filled Unicode pieces (default)
  classic: {
    name: 'Classic',
    description: 'Traditional filled pieces',
    whitePieces: {
      [PieceType.King]: '♔',
      [PieceType.Queen]: '♕',
      [PieceType.Rook]: '♖',
      [PieceType.Bishop]: '♗',
      [PieceType.Knight]: '♘',
      [PieceType.Pawn]: '♙',
    },
    blackPieces: {
      [PieceType.King]: '♚',
      [PieceType.Queen]: '♛',
      [PieceType.Rook]: '♜',
      [PieceType.Bishop]: '♝',
      [PieceType.Knight]: '♞',
      [PieceType.Pawn]: '♟',
    },
  },

  // Modern outlined style with bolder appearance
  modern: {
    name: 'Modern',
    description: 'Bold outlined pieces',
    whitePieces: {
      [PieceType.King]: '♔',
      [PieceType.Queen]: '♕',
      [PieceType.Rook]: '♖',
      [PieceType.Bishop]: '♗',
      [PieceType.Knight]: '♘',
      [PieceType.Pawn]: '♙',
    },
    blackPieces: {
      [PieceType.King]: '♚',
      [PieceType.Queen]: '♛',
      [PieceType.Rook]: '♜',
      [PieceType.Bishop]: '♝',
      [PieceType.Knight]: '♞',
      [PieceType.Pawn]: '♟',
    },
    fontWeight: 'bold',
    fontSize: '3.8rem',
  },

  // Alpha notation style (letters for pieces)
  alpha: {
    name: 'Alpha',
    description: 'Letter notation (K, Q, R, B, N, P)',
    whitePieces: {
      [PieceType.King]: 'K',
      [PieceType.Queen]: 'Q',
      [PieceType.Rook]: 'R',
      [PieceType.Bishop]: 'B',
      [PieceType.Knight]: 'N',
      [PieceType.Pawn]: 'P',
    },
    blackPieces: {
      [PieceType.King]: 'K',
      [PieceType.Queen]: 'Q',
      [PieceType.Rook]: 'R',
      [PieceType.Bishop]: 'B',
      [PieceType.Knight]: 'N',
      [PieceType.Pawn]: 'P',
    },
    fontFamily: 'Arial, sans-serif',
    fontWeight: 'bold',
    fontSize: '2rem',
  },

  // Unicode chess symbols in different case
  cases: {
    name: 'Cases',
    description: 'Alternative chess symbols',
    whitePieces: {
      [PieceType.King]: '♔',
      [PieceType.Queen]: '♕',
      [PieceType.Rook]: '♖',
      [PieceType.Bishop]: '♗',
      [PieceType.Knight]: '♘',
      [PieceType.Pawn]: '♙',
    },
    blackPieces: {
      [PieceType.King]: '♚',
      [PieceType.Queen]: '♛',
      [PieceType.Rook]: '♜',
      [PieceType.Bishop]: '♝',
      [PieceType.Knight]: '♞',
      [PieceType.Pawn]: '♟',
    },
    fontSize: '4rem',
  },
};

/**
 * Piece Set Manager - handles piece set selection and persistence
 */
export class PieceSetManager {
  private static readonly STORAGE_KEY = 'chess-piece-set';
  private static readonly DEFAULT_PIECE_SET: PieceSet = 'classic';
  
  private currentSet: PieceSet;

  constructor() {
    this.currentSet = this.loadPieceSet();
    this.applyPieceSet(this.currentSet);
  }

  /**
   * Load piece set preference from localStorage
   */
  private loadPieceSet(): PieceSet {
    try {
      const stored = localStorage.getItem(PieceSetManager.STORAGE_KEY);
      if (stored && this.isValidPieceSet(stored)) {
        return stored as PieceSet;
      }
    } catch (error) {
      console.warn('Failed to load piece set from localStorage:', error);
    }
    return PieceSetManager.DEFAULT_PIECE_SET;
  }

  /**
   * Save piece set preference to localStorage
   */
  private savePieceSet(pieceSet: PieceSet): void {
    try {
      localStorage.setItem(PieceSetManager.STORAGE_KEY, pieceSet);
    } catch (error) {
      console.warn('Failed to save piece set to localStorage:', error);
    }
  }

  /**
   * Check if a string is a valid piece set
   */
  private isValidPieceSet(value: string): boolean {
    return value in PIECE_SETS;
  }

  /**
   * Apply piece set CSS custom properties to document root
   */
  private applyPieceSet(pieceSet: PieceSet): void {
    const config = PIECE_SETS[pieceSet];
    const root = document.documentElement;

    // Apply font styling
    root.style.setProperty('--piece-font-family', config.fontFamily || 'inherit');
    root.style.setProperty('--piece-font-size', config.fontSize || '3.5rem');
    root.style.setProperty('--piece-font-weight', config.fontWeight || 'normal');

    // Store piece set name for components to access
    root.setAttribute('data-piece-set', pieceSet);
  }

  /**
   * Get the current piece set
   */
  getPieceSet(): PieceSet {
    return this.currentSet;
  }

  /**
   * Set a new piece set
   */
  setPieceSet(pieceSet: PieceSet): void {
    if (!this.isValidPieceSet(pieceSet)) {
      console.warn(`Invalid piece set: ${pieceSet}`);
      return;
    }

    this.currentSet = pieceSet;
    this.applyPieceSet(pieceSet);
    this.savePieceSet(pieceSet);
  }

  /**
   * Get the symbol for a specific piece
   */
  getPieceSymbol(type: PieceType, color: Color): string {
    const config = PIECE_SETS[this.currentSet];
    return color === Color.White
      ? config.whitePieces[type]
      : config.blackPieces[type];
  }

  /**
   * Get the configuration for the current piece set
   */
  getCurrentConfig(): PieceSetConfig {
    return PIECE_SETS[this.currentSet];
  }
}

// Export singleton instance
export const pieceSetManager = new PieceSetManager();
